import {
  ChartDataState,
  FeatureState,
  FooterState,
  GlobalStyleState,
  ModalState,
  ProductFeatureDataState,
  ProductKindFilterState,
  ProductState,
  SectionState,
  StatsState,
  SubSectionState,
} from "../store/chart";
import {
  CmsChartData,
  CmsFeature,
  CmsProduct,
  CmsProductFeature,
  CmsSubSection,
} from "../services/cms";
import {
  FontFamily,
  UIButton,
  UIColor,
  UIFont,
  UIImage,
  UITag,
  UIText,
} from "../types";

const fontFamilyMap: { [key: string]: FontFamily } = {
  HarmoniaSans: FontFamily.HarmoniaSansStd,
  HarmoniaSansStd: FontFamily.HarmoniaSansStd,
  HarmoniaSansPro: FontFamily.HarmoniaSansStd,
};

export function transformQuizFromBE(cmsData: CmsChartData): ChartDataState {
  const { comparison_chart } = cmsData;
  return {
    styling: getStyling(cmsData),
    backgroundColor: getColor(comparison_chart.desktop_column_color),
    showOnlyDiffsToggle: {
      label: {
        value:
          cmsData.labels.find((label) => label.name === "show_only_differences")
            ?.text || "Show Only Differences",
      },
    },
    showOnlyDiffs: false,
    header: {
      value: comparison_chart.header,
      font: getFont(comparison_chart.header_font),
      color: getColor(comparison_chart.header_text_color),
    },
    instructions: {
      value: comparison_chart.instructions_copy,
      font: getFont(comparison_chart.instructions_copy_text_font),
      color: getColor(comparison_chart.instructions_copy_text_color),
    },
    legalCopy: comparison_chart.legal_copy,
    useCustomProductFilters: comparison_chart.use_custom_product_filters,
    productKindFilters: getProductKindFilters(cmsData),
    showAdditionalFilterButton: comparison_chart.show_additional_filter_button,
    additionalFilterButton: getAdditionalFilterButton(cmsData),
    modal: getModal(cmsData),
    showProductApplication: comparison_chart.show_product_application,
    productApplicationTitle: getText(
      comparison_chart.product_application_title,
      comparison_chart.product_application_title_font,
      comparison_chart.product_application_title_text_color,
    ),
    showBestFor: comparison_chart.show_best_for_section,
    bestForTitle: getText(
      comparison_chart.best_for_section_title,
      comparison_chart.best_for_section_title_font,
      comparison_chart.best_for_section_title_text_color,
    ),
    bestForIconColor: getColor(comparison_chart.best_for_section_icon_color),
    bestForHoverIconColor: getColor(
      comparison_chart.best_for_section_hover_icon_color,
    ),
    showPrice: comparison_chart.show_price,
    showShopButton: comparison_chart.show_shop_buttons,
    showShopSelectSection: comparison_chart.show_shop_select_section,
    shopSelectTitle: getText(
      comparison_chart.shop_select_title,
      comparison_chart.shop_select_title_font,
      comparison_chart.shop_select_title_text_color,
    ),
    shopSelectMobileTitleTextColor: getColor(
      comparison_chart.shop_select_mobile_title_text_color,
    ),
    shopSelectMobileTitleRowColor: getColor(
      comparison_chart.shop_select_mobile_title_row_color,
    ),
    shopSelectDescription: getText(
      comparison_chart.shop_select_description,
      comparison_chart.shop_select_description_font,
      comparison_chart.shop_select_description_text_color,
    ),
    shopSelectOpenOnLoad: comparison_chart.open_on_load,
    products: getProducts(cmsData),
    sections: getSections(cmsData),
    showStats: comparison_chart.show_stats_sections,
    stats: getStats(cmsData),
    showFooter: comparison_chart.show_footer_section,
    footer: getFooter(cmsData),
  };
}

export function transformQuizFromMocks(data: ChartDataState): ChartDataState {
  return data;
}

function getStyling(cmsData: CmsChartData): GlobalStyleState {
  const { comparison_chart } = cmsData;
  return {
    filterButton: {
      font: getFont(comparison_chart.filter_button_font),
      backgroundColor: getColor(
        comparison_chart.filter_button_background_color,
      ),
      borderColor: getColor(comparison_chart.filter_button_outline_color),
      textColor: getColor(comparison_chart.filter_button_text_color),
      selected: {
        backgroundColor: getColor(
          comparison_chart.filter_button_selected_background_color,
        ),
        textColor: getColor(comparison_chart.filter_button_selected_text_color),
        borderColor: getColor(comparison_chart.filter_button_outline_color),
      },
    },
    tag: {
      font: getFont(comparison_chart.tag_font),
    },
    productApplication: {
      font: getFont(comparison_chart.product_application_description_font),
      textColor: getColor(
        comparison_chart.product_application_description_text_color,
      ),
    },
    legalCopy: {
      textColor: getColor(comparison_chart.legal_footer_text_color),
    },
    column: {
      backgroundColor: getColor(comparison_chart.column_background_color),
      bundleBackgroundColor: getColor(
        comparison_chart.bundle_secondary_column_background_color,
      ),
    },
    shopButton: {
      copy: comparison_chart.shop_button_copy,
      textColor: getColor(comparison_chart.shop_button_text_primary_color),
      borderColor: getColor(comparison_chart.shop_button_primary_border_color),
      backgroundColor: getColor(comparison_chart.shop_button_primary_color),
      hover: {
        textColor: getColor(comparison_chart.shop_button_text_hover_color),
        borderColor: getColor(comparison_chart.shop_button_hover_border_color),
        backgroundColor: getColor(comparison_chart.shop_button_hover_color),
      },
    },
    shopSelectButton: {
      textColor: getColor(
        comparison_chart.shop_select_button_text_primary_color,
      ),
      borderColor: getColor(
        comparison_chart.shop_select_button_primary_border_color,
      ),
      backgroundColor: getColor(
        comparison_chart.shop_select_button_primary_color,
      ),
      hover: {
        textColor: getColor(
          comparison_chart.shop_select_button_text_hover_color,
        ),
        borderColor: getColor(
          comparison_chart.shop_select_button_hover_border_color,
        ),
        backgroundColor: getColor(
          comparison_chart.shop_select_button_hover_color,
        ),
      },
    },
  };
}

function getAdditionalFilterButton(
  cmsData: CmsChartData,
): UIButton | undefined {
  const { comparison_chart, additional_filter_link } = cmsData;
  const label = getText(
    comparison_chart.additional_filter_button_copy,
    comparison_chart.filter_button_font,
    comparison_chart.additional_filter_button_text_color,
  );
  if (label && additional_filter_link) {
    return {
      label,
      link: additional_filter_link,
      backgroundColor: getColor(
        comparison_chart.additional_filter_button_background_color,
      ),
      borderColor: getColor(
        comparison_chart.additional_filter_button_outline_color,
      ),
    };
  }
}

function getMobileAdditionalFilterButton(
  cmsData: CmsChartData,
): UIButton | undefined {
  const { comparison_chart, additional_filter_link } = cmsData;
  const label = getText(
    comparison_chart.additional_filter_button_copy,
    comparison_chart.filter_button_font,
    comparison_chart.product_additional_filter_button_text_color,
  );
  if (label && additional_filter_link) {
    const color = getColor(
      comparison_chart.product_additional_filter_button_color,
    );
    return {
      label,
      link: additional_filter_link,
      backgroundColor: color,
      borderColor: color,
    };
  }
}

function getProductKindFilters(
  cmsData: CmsChartData,
): ProductKindFilterState[] {
  const { comparison_chart } = cmsData;
  return comparison_chart.chart_carousel_sections.map((section) => {
    return {
      id: section.uuid,
      productKindId: section.product_kind?.uuid,
      filterButtonCopy: section.filter_button_copy,
      categoryTitle: section.category_title,
    };
  });
}

function getModal(cmsData: CmsChartData): ModalState {
  const { comparison_chart } = cmsData;
  return {
    backgroundColor: getColor(comparison_chart.product_pop_up_background_color),
    footerBackgroundColor: getColor(
      comparison_chart.product_popup_footer_background_color,
    ),
    textColor: getColor(comparison_chart.product_text_color),
    selectedTextColor: getColor(comparison_chart.product_selected_text_color),
    selectedBackgroundColor: getColor(
      comparison_chart.product_selected_state_color,
    ),
    openButton: {
      label: {
        value: comparison_chart.more_products_button_copy,
        color: getColor(comparison_chart.more_products_button_text_color),
      },
      backgroundColor: getColor(comparison_chart.more_products_button_color),
    },
    showAdditionalFilterButton: comparison_chart.show_additional_filter_button,
    additionalFilterButton: getMobileAdditionalFilterButton(cmsData),
    closeButton: {
      label: {
        value: comparison_chart.close_product_pop_up_button_copy,
        color: getColor(
          comparison_chart.close_product_pop_up_button_text_color,
        ),
      },
      backgroundColor: getColor(
        comparison_chart.close_product_pop_up_button_color,
      ),
    },
  };
}

function getProducts(cmsData: CmsChartData): ProductState[] {
  const { comparison_chart } = cmsData;
  return comparison_chart.products.map((product) => {
    const base = {
      id: product.uuid,
      name: product.name,
      sku: product.sku,
      exploreSku: product.explore_sku,
      productKindId: product.product_kind.uuid,
      desktopPreselected: product.desktop_preselected,
      mobilePreselected: product.mobile_preselected,
      useTag: product.use_tag,
      tag: getTag(product),
      price: product.price,
      savePrice: product.sales_price,
      useSalesMessage: product.use_sales_message,
      salesMessage: getText(
        product.sales_message,
        product.sales_message_text_font,
        product.sales_message_text_color,
      ),
      application: product.application,
      bestForIcons: product.product_best_for_icons?.map((icon) => ({
        url: icon.best_for_icon.icon_url,
        alt: icon.best_for_icon.copy,
      })),
      overview: product.overview,
      shopUrl: product.link,
      showShopSelectButton: product.show_shop_select_button,
      shopSelectButton: {
        description: product.shop_select_description,
        url: product.shop_select_page_url,
        buttonCopy: product.shop_select_button_copy,
      },
    };
    if (product.bundle && product.bundle.left && product.bundle.right) {
      return {
        ...base,
        isBundle: true,
        leftData: {
          id: product.bundle.left.uuid,
          image: product.bundle.left.image_url,
          features: product.bundle.left.product_features.map(getProductFeature),
        },
        rightData: {
          id: product.bundle.right.uuid,
          image: product.bundle.right.image_url,
          features:
            product.bundle.right.product_features.map(getProductFeature),
        },
      };
    }
    return {
      ...base,
      isBundle: false,
      data: {
        image: product.image_url,
        features: product.product_features.map(getProductFeature),
      },
    };
  });
}

function getTag(product: CmsProduct): UITag | undefined {
  const text = getText(product.tag_name, undefined, product.tag_text_color);
  const background = getColor(product.tag_background_color);
  if (text && background) {
    return {
      text,
      background,
    };
  }
}

function getProductFeature(
  productFeature: CmsProductFeature,
): ProductFeatureDataState {
  return {
    id: productFeature.uuid,
    featureId: productFeature.feature.uuid,
    featureItemId: productFeature.selected_option?.uuid,
    icon: getImage(
      productFeature.selected_option?.image_url,
      productFeature.selected_option?.alt_tag,
    ),
    item: productFeature.selected_option?.name,
    subItem: productFeature.selected_option?.sub_item,
    itemLegalCopy: productFeature.selected_option?.legal_copy,
  };
}

function getSections(cmsData: CmsChartData): SectionState[] {
  return cmsData.comparison_chart.sections.map((section) => {
    return {
      id: section.uuid,
      name: section.name,
      font: getFont(section.font),
      desktopTextColor: getColor(section.font_color),
      mobileTextColor: getColor(section.mobile_title_color),
      mobileBackgroundColor: getColor(section.mobile_title_row_color),
      collapsed: section.collapsed,
      showProductOverview: section.show_product_overview,
      productOverviewTitle: section.product_overview_title_copy,
      subSections: section.subsections.map(getSubSection),
    };
  });
}

function getSubSection(subSection: CmsSubSection): SubSectionState {
  return {
    id: subSection.uuid,
    name: subSection.name,
    font: getFont(subSection.font),
    desktopTextColor: getColor(subSection.font_color),
    mobileTextColor: getColor(subSection.mobile_title_color),
    mobileBackgroundColor: getColor(subSection.mobile_title_row_color),
    features: subSection.features.map(getFeature),
  };
}

function getFeature(feature: CmsFeature): FeatureState {
  return {
    id: feature.feature.uuid,
    name: feature.feature.name,
    tooltip: {
      title: feature.feature.tooltip_title,
      description: feature.feature.tooltip_description,
      visible: feature.feature.show_tooltip,
    },
    icon: getImage(feature.feature.icon_url, feature.feature.alt_tag),
    legalCopy: feature.feature.legal_copy,
  };
}

function getStats(cmsData: CmsChartData): StatsState[] | undefined {
  return cmsData.comparison_chart.stats_sections?.map((section) => ({
    title: {
      value: section.title,
      font: getFont(section.title_font),
      color: getColor(section.title_text_color),
    },
    description: {
      value: section.description,
      font: getFont(section.description_font),
      color: getColor(section.description_text_color),
    },
    img: getImage(section.image_url, section.image_alt),
  }));
}

function getFooter(cmsData: CmsChartData): FooterState | undefined {
  const { comparison_chart } = cmsData;
  if (comparison_chart.footer_title && comparison_chart.footers) {
    return {
      title: {
        value: comparison_chart.footer_title,
        font: getFont(comparison_chart.footer_title_font),
        color: getColor(),
      },
      logos: comparison_chart.footers.map((image) => ({
        url: image.image_url,
        alt: image.image_alt,
      })),
    };
  }
}

function getFont(font?: string): UIFont | undefined {
  if (font) {
    return {
      family: font ? fontFamilyMap[font] : undefined,
    };
  }
}

function getColor(color?: string): UIColor | undefined {
  if (color) {
    return {
      hex: color,
    };
  }
}

function getImage(url?: string, alt?: string): UIImage | undefined {
  if (url) {
    return {
      url,
      alt,
    };
  }
}

function getText(
  text?: string,
  font?: string,
  color?: string,
  hoverColor?: string,
): UIText | undefined {
  if (text) {
    return {
      value: text,
      font: getFont(font),
      color: getColor(color),
      hoverColor: getColor(hoverColor),
    };
  }
}
